import React, { useEffect, useMemo, useState } from 'react';
import './style.scss';
import SubscriptionUtils from 'services/SubscriptionUtils';
import PatientBadge from 'components/Patient/PatientBadge';
import CalendarUtils from 'services/CalendarUtils';
import ProgressMessage from 'components/Utils/ProgressMessage';
import List from '@material-ui/core/List';
import DefaultButton from 'components/Utils/NewDefaultButton';
import GenericBadge from 'components/Utils/GenericBadge';
import SelectableListItem from 'components/Utils/SelectableListItem';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultModal from 'components/Utils/DefaultModal';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import ValidatorUtils from 'services/ValidatorUtils';
import _ from 'lodash';
import PerformanceUtils from 'services/PerformanceUtils';
import Icon from 'components/Utils/NewIcon';
import PatientSearchModal from 'components/Modals/PatientSearchModal';
import MenuItem from '@material-ui/core/MenuItem';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { DefaultSwitch } from 'services/UIUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import ConfirmationIcon from 'components/Utils/NewIcon/ConfirmationIcon';

const INPUT_STYLE = { display: 'inline-block', marginRight: '15px', width: '200px' };
const DEFAULT_ARRAY = [];
const EMAIL_TEMPLATES = [
  {
    name: 'none',
    description: 'No email',
  },
  {
    name: 'subscription',
    description: 'Complete email with Subscription information',
  },
  {
    name: 'code',
    description: 'Simple email to add code through Settings',
  },
  {
    name: 'nia_health',
    description: 'Nia Health email',
  },
];

const CorporateBatches = ({ corporation }) => {
  const corporationId = corporation.id;
  const dispatch = useAppDispatch();

  const corporatePatients = useAppSelector((state) => state.corporations.patients);
  const corporateCodes = useAppSelector((state) => state.corporations.codes);
  const corporateBatches = useAppSelector((state) => state.corporations.batches);
  const unpaidBatches = useAppSelector((state) => state.corporations.unpaidBatches);
  const patients = corporatePatients[corporationId] || DEFAULT_ARRAY;
  const codes = corporateCodes[corporationId] || DEFAULT_ARRAY;
  const batches = corporateBatches[corporationId] || DEFAULT_ARRAY;

  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState();

  useEffect(() => {
    setLoading(true);

    Promise.all([
      dispatch(api.Corporations.getCorporationPatients({ id: corporationId })),
      dispatch(api.Corporations.getCorporationBatches({ id: corporationId })),
      dispatch(api.Corporations.getCorporationCodes({ id: corporationId })),
    ]).finally(() => {
      setLoading(false);
    });
  }, [dispatch, corporationId]);

  const data = useMemo(() => {
    return PerformanceUtils.sortBy(
      batches.map((batch) => {
        let batchCodes = codes
          .filter((code) => code.batch_id === batch.id)
          .map((code) => {
            let patient = patients.find((patient) => patient.patient.id === code.patient_id);
            return {
              ...code,
              patient: patient ? patient.patient : undefined,
              subscription: patient ? patient.subscription : undefined,
            };
          });

        return {
          batch,
          codes: batchCodes,
          numberPatients: batchCodes.filter((code) => !!code.patient).length,
        };
      }),
      ['batch.date'],
    );
  }, [patients, codes, batches]);

  const selectedBatch = useMemo(() => data.find((d) => d.batch.id === selected), [data, selected]);

  return (
    <div className="corporate-batches">
      <div className="full-height-panel underside-right-panel scroll-list !w-[260px]">
        <ProgressMessage show={loading} topLeft={true} />
        <div className="top-box padded-box">
          <span className="large-title">Batches</span>
          <AddNewBatch corporation={corporation} />
          {!loading && data.length === 0 && (
            <span className="small-title padded-title">No batches found</span>
          )}
        </div>
        <List>
          {data.map((batchData) => {
            let title = `${CalendarUtils.format(batchData.batch.date, 'YYYY/MM/DD')}`;
            if (batchData.status) title += ` (${batchData.status})`;
            let info = `${batchData.numberPatients}/${batchData.codes.length}`;
            const isOverdue = (unpaidBatches[corporationId] || []).includes(batchData.batch.id);

            if (isOverdue) {
              info = (
                <React.Fragment>
                  {info}
                  <Icon
                    src="triangle-exclamation"
                    className="red"
                    alt="Overdue"
                    showTooltip={true}
                    width="22px"
                    style={{ position: 'absolute', top: 0, right: 0 }}
                  />
                </React.Fragment>
              );
            }
            return (
              <SelectableListItem
                selected={selected === batchData.batch.id}
                divider={true}
                onClick={() => setSelected(batchData.batch.id)}
                item={<GenericBadge title={title} info={info} />}
              />
            );
          })}
        </List>
      </div>
      {!!selected && <BatchDetails batchData={selectedBatch} key={selected} />}
    </div>
  );
};

const BatchDetails = ({ batchData = {} }) => {
  let { codes = [], batch = {} } = batchData;

  const dispatch = useAppDispatch();

  const [value, setValue] = useState({ ...batch });
  const [updatingBatch, setUpdatingBatch] = useState(false);
  const [removingCode, setRemovingCode] = useState();
  const [addingPatient, setAddingPatient] = useState();
  const [patientModal, setPatientModal] = useState();

  function onChangeInvoice(event, i) {
    let invoices = [...value.invoices];
    invoices[i] = { ...invoices[i], [event.target.name]: event.target.value };
    setValue({ ...value, invoices });
  }

  function addLine() {
    let newValue = { ...value, invoices: [...(value.invoices || []), {}] };
    setValue(newValue);
  }

  async function updateBatch() {
    setUpdatingBatch(true);
    await dispatch(
      api.Corporations.updateCorporationBatch({
        id: batch.corporation_id,
        batchId: batch.id,
        corporationBatch: value,
      }),
      {
        errorTitle: 'Update Batch',
      },
    );
    dispatch(api.Corporations.getUnpaidBatches());
    setUpdatingBatch(false);
  }

  function removeInvoice(i) {
    let invoices = [...value.invoices];
    invoices.splice(i, 1);
    setValue({ ...value, invoices });
  }

  async function removeCode(code) {
    setRemovingCode(code.id);
    await dispatch(
      api.Corporations.removeCorporationCode({ id: code.corporation_id, codeId: code.id }),
      { title: 'Remove Code' },
    );
    setRemovingCode();
  }

  async function addPatient(patient, code = {}) {
    setPatientModal();
    if (patient) {
      setAddingPatient(code.id);
      let { id, corporation_id } = code;
      await dispatch(
        api.Corporations.addPatientToCorporationCode({
          id: corporation_id,
          codeId: id,
          patientId: patient.id,
        }),
        {
          errorTitle: 'Add Patient',
        },
      );
      setAddingPatient();
      dispatch(api.Corporations.getCorporationPatients({ id: corporation_id }));
    }
  }

  return (
    <div className="details-panel padded-box scroll-list flex-grow">
      <span className="large-title">Batch Details</span>
      <div className="row">
        <DefaultTextField
          label="Date created"
          value={CalendarUtils.format(batch.date, 'YYYY/MM/DD')}
          disabled={true}
          style={INPUT_STYLE}
        />
        <DefaultSwitch
          name="disabled"
          onChange={(event, isInputChecked) => {
            setValue((prevState) => {
              return {
                ...prevState,
                disabled: isInputChecked,
              };
            });
          }}
          label="Disabled"
          padded={false}
          checked={!!value.disabled}
        />
      </div>
      <span className="large-title">Paid Invoices</span>
      <List>
        {(value.invoices || []).map((invoice, i) => (
          <div className="row" key={i + '' + (value.invoices || []).length}>
            <DefaultDatePicker
              name="date"
              label="Date"
              value={invoice.date}
              onChange={(event, value) => {
                let change = { target: { name: 'date', value } };
                onChangeInvoice(change, i);
              }}
              style={{ width: '150px', marginRight: '15px' }}
            />
            <DefaultTextField
              name="number"
              label="Invoice #"
              value={invoice.number}
              onChange={(event) => onChangeInvoice(event, i)}
              style={{ width: '150px', marginRight: '15px' }}
            />
            <DefaultTextField
              name="amount"
              label="Amount"
              value={invoice.amount}
              style={{ width: '100px' }}
              onChange={(event) => onChangeInvoice(event, i)}
            />
            <div style={{ position: 'relative', top: '23px', left: '20px' }}>
              <Icon
                src="circle-trash"
                alt="Delete Invoice"
                showTooltip={true}
                width="25px"
                style={{ display: 'inline-block', margin: 'initial', marginRight: '15px' }}
                onClick={() => removeInvoice(i)}
              />
              {i === (value.invoices || []).length - 1 && (
                <Icon
                  src="circle-plus"
                  className="blue"
                  width="25px"
                  alt="Add New Invoice"
                  showTooltip={true}
                  style={{ display: 'inline-block' }}
                  onClick={addLine}
                />
              )}
            </div>
          </div>
        ))}
        {(value.invoices || []).length === 0 && (
          <Icon
            src="circle-plus"
            className="blue mt-1"
            width="25px"
            alt="Add New Invoice"
            showTooltip={true}
            style={{ display: 'inline-block' }}
            onClick={addLine}
          />
        )}
      </List>
      <DefaultButton
        name="Save Invoice Changes"
        onClick={updateBatch}
        loading={updatingBatch}
        disabled={!!PerformanceUtils.isEqual(batch, value)}
        style={{ width: '250px' }}
      />
      <span className="large-title">Patients</span>
      <table>
        <thead>
          <th>Code</th>
          <th>Email</th>
          <th>Patient</th>
          <th>Subscription</th>
          <th>&nbsp;</th>
        </thead>
        <tbody>
          {codes.map((code) => {
            return (
              <tr key={code.id}>
                <td width="10%">{code.id}</td>
                <td width="20%">{code.email}</td>
                <td width="40%">
                  {!!code.patient_id && (
                    <>
                      <ProgressMessage show={!code.patient} />
                      {!!code.patient && <PatientBadge patient={code.patient} />}
                    </>
                  )}
                  {!code.patient_id && (
                    <>
                      <Icon
                        src="circle-plus"
                        className="blue"
                        width="25px"
                        alt="Add Patient"
                        showTooltip={true}
                        loading={addingPatient === code.id}
                        style={{ float: 'left' }}
                        onClick={() => setPatientModal(code)}
                      />
                      <PatientSearchModal
                        onSelect={(patient) => addPatient(patient, code)}
                        onClose={() => setPatientModal()}
                        open={!!patientModal && patientModal === code}
                      />
                    </>
                  )}
                </td>
                <td width="25%">
                  {!!code.subscription && (
                    <span>{SubscriptionUtils.getSubscriptionName(code.subscription)}</span>
                  )}
                </td>
                <td width="5%">
                  <ConfirmationIcon
                    src="circle-trash"
                    alt="Delete Code"
                    showTooltip={true}
                    width="25px"
                    onClick={() => removeCode(code)}
                    loading={removingCode === code.id}
                    confirmText="Are you sure you want to remove the code? This will dissociate any patient that has claimed this code from this corporate client. Patient subscriptions must be changed manually."
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const DEFAULT_BATCH = { invoice: {}, emails: '', sendEmails: true };

const AddNewBatch = ({ corporation = {} }) => {
  const dispatch = useAppDispatch();
  const corporationId = corporation.id;

  const [value, setValue] = useState(DEFAULT_BATCH);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  function onChangeInvoice(event) {
    setValue({
      ...value,
      invoice: {
        ...value.invoice,
        [event.target.name]: event.target.value,
      },
    });
  }

  function onChangeValue(event) {
    setValue({
      ...value,
      [event.target.name]: event.target.value,
    });
  }

  const emails = useMemo(() => {
    return value.emails
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => !!line)
      .filter((line) => ValidatorUtils.checkIsEmail(line.trim()));
  }, [value.emails]);

  const emailDescription = useMemo(() => {
    let description = 'Please add one email per line';

    let split = value.emails.trim().split('\n');

    if (value.emails.includes(',')) {
      description += '. Do not include separators, such as commas.';
    } else if (_.uniq(emails).length !== emails.length) {
      description = `Warning: Found duplicate emails!`;
    } else if (value.emails.trim().length > 0 && split.length !== emails.length) {
      description = `Warning: Some lines do not contain emails!`;
    } else if (emails.length > 0) {
      description = `Found ${emails.length} valid emails`;
    }
    return description;
  }, [value.emails, emails]);

  const buttonDisabled =
    emails.length === 0 ||
    !emailDescription.includes('valid emails') ||
    !value.invoice.date ||
    !value.invoice.number ||
    !value.invoice.amount ||
    !value.email;

  async function addBatch() {
    setLoading(true);
    let email = value.email;
    if (email === 'none') email = undefined;
    let corporationBatchAdd = {
      invoice: value.invoice,
      email,
      emails,
    };

    const result = await dispatch(
      api.Corporations.addCorporationBatch({ id: corporation.id, corporationBatchAdd }),
      {
        errorTitle: 'New Batch',
      },
    );

    setLoading(false);
    if (result) {
      setValue(DEFAULT_BATCH);
      dispatch(api.Corporations.getCorporationCodes({ id: corporationId }));
      setShowModal(false);
    }
  }

  return (
    <>
      <DefaultButton
        name="Add New Batch"
        onClick={() => setShowModal(true)}
        icon="layer-group-plus"
        style={INPUT_STYLE}
      />
      {showModal && (
        <DefaultModal
          key="new-batch-modal"
          wrap={true}
          padding={true}
          title="Add New Batch"
          content={
            <div className="add-new-modal">
              <div className="small-title">Invoice</div>
              <div className="row">
                <DefaultDatePicker
                  name="date"
                  label="Date"
                  value={value.invoice.date}
                  onChange={(event, value) => {
                    let change = { target: { name: 'date', value } };
                    onChangeInvoice(change);
                  }}
                  style={{ width: '40%', marginRight: '5%' }}
                  disabled={loading}
                />
                <DefaultTextField
                  name="number"
                  label="Invoice #"
                  value={value.invoice.number}
                  onChange={onChangeInvoice}
                  style={{ width: '30%', marginRight: '5%' }}
                  disabled={loading}
                />
                <DefaultTextField
                  name="amount"
                  label="Amount"
                  value={value.invoice.amount}
                  style={{ width: '20%' }}
                  onChange={onChangeInvoice}
                  disabled={loading}
                />
              </div>
              <div className="small-title">Emails</div>
              <div className="row">
                <TextareaAutosize
                  name="emails"
                  value={value.emails}
                  disabled={loading}
                  onChange={onChangeValue}
                />
              </div>
              <span style={{ margin: '15px 0', display: 'block' }}>{emailDescription}</span>
              <div className="row">
                <DefaultSelectField
                  style={{ width: '100%' }}
                  label="Email Template"
                  value={value.email}
                  onChange={(event) => {
                    event.target = {
                      name: 'email',
                      value: event.target.value,
                    };
                    onChangeValue(event);
                  }}
                  inputElements={(EMAIL_TEMPLATES || []).map((email) => (
                    <MenuItem key={'select-' + email.name} value={email.name}>
                      {email.description}
                    </MenuItem>
                  ))}
                />
              </div>
              <div className="row pt-3">
                <DefaultButton
                  name="Save Batch"
                  onClick={addBatch}
                  loading={loading}
                  disabled={buttonDisabled}
                  style={{ width: '75%', margin: 'auto' }}
                />
              </div>
            </div>
          }
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default CorporateBatches;
